import { addDoc, collection, deleteDoc, deleteField, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, startAfter, Timestamp, updateDoc, writeBatch } from 'firebase/firestore';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import type {
    JourneyChapterProgressFirestore,
    JourneyChapterTemplateFirestore,
    JourneyItemProgressFirestore,
    JourneyItemRewardStats,
    JourneyLeaderboardEntry,
    JourneyItemTemplateFirestore,
    JourneyItemType,
    JourneySectionOption,
    JourneySectionProgressFirestore,
    JourneySectionTemplateFirestore,
    JourneyState,
    UserJourneyTree,
} from './journeyTypes';
import { JOURNEY_ITEM_TYPES } from '../utils/journeyItemTypeDefaults';
import {
    JourneyChapterStatus,
    JourneyItemStatus,
    JourneySectionStatus,
} from './journeyTypes';

const COLLECTION_NAME = 'journey_templates';
const MAX_BATCH_SIZE = 500;

const compileUserChapterCall = createApiCall<{ chapterNumber: number }, unknown>(
    'compileJourneyChapterForUser',
);

const createEmptyRewardStats = (type: JourneyItemType): JourneyItemRewardStats => ({
    type,
    count: 0,
    minBrainPoints: 0,
    maxBrainPoints: 0,
    minEnergyCost: 0,
    maxEnergyCost: 0,
});

export class JourneyService {
    private static instance: JourneyService;

    private constructor() {}

    public static getInstance(): JourneyService {
        if (!JourneyService.instance) {
            JourneyService.instance = new JourneyService();
        }
        return JourneyService.instance;
    }

    public async getChapters(): Promise<JourneyChapterTemplateFirestore[]> {
        const q = query(collection(db, COLLECTION_NAME), orderBy('number', 'asc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
        })) as JourneyChapterTemplateFirestore[];
    }

    public async getSections(chapterId: string): Promise<JourneySectionTemplateFirestore[]> {
        const q = query(
            collection(db, COLLECTION_NAME, chapterId, 'sections'),
            orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
        })) as JourneySectionTemplateFirestore[];
    }

    public async getItems(chapterId: string, sectionId: string): Promise<JourneyItemTemplateFirestore[]> {
        const q = query(
            collection(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items'),
            orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
        })) as JourneyItemTemplateFirestore[];
    }

    public async updateChapter(chapter: JourneyChapterTemplateFirestore): Promise<void> {
        const chapterRef = doc(db, COLLECTION_NAME, chapter.id!);
        const { id, ...dataWithoutId } = chapter;
        void id;
        await updateDoc(chapterRef, { ...dataWithoutId, updatedAt: serverTimestamp() });
    }

    public async updateSection(chapterId: string, section: JourneySectionTemplateFirestore): Promise<void> {
        const sectionRef = doc(db, COLLECTION_NAME, chapterId, 'sections', section.id!);
        const { id, ...dataWithoutId } = section;
        void id;
        await updateDoc(sectionRef, { ...dataWithoutId, updatedAt: serverTimestamp() });
    }

    public async createItem(chapterId: string, sectionId: string, item: JourneyItemTemplateFirestore): Promise<JourneyItemTemplateFirestore> {
        const itemsRef = collection(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items');
        const { id, ...dataWithoutId } = item;
        void id;
        const docRef = await addDoc(itemsRef, { ...dataWithoutId, updatedAt: serverTimestamp() });
        return { ...item, id: docRef.id };
    }

    public async updateItem(chapterId: string, sectionId: string, item: JourneyItemTemplateFirestore): Promise<void> {
        const itemRef = doc(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items', item.id!);
        const { id, ...dataWithoutId } = item;
        void id;
        await updateDoc(itemRef, { ...dataWithoutId, updatedAt: serverTimestamp() });
    }

    public async deleteItem(chapterId: string, sectionId: string, itemId: string): Promise<void> {
        await deleteDoc(doc(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items', itemId));
    }

    public async updateItemOrders(chapterId: string, sectionId: string, items: JourneyItemTemplateFirestore[]): Promise<void> {
        await Promise.all(
            items.map((item) => {
                const itemRef = doc(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items', item.id!);
                return updateDoc(itemRef, { order: item.order });
            }),
        );
    }

    private async forEachTemplateItem(
        callback: (item: JourneyItemTemplateFirestore, chapterId: string, sectionId: string) => void,
    ): Promise<void> {
        const chapters = await this.getChapters();
        for (const chapter of chapters) {
            const sections = await this.getSections(chapter.id!);
            for (const section of sections) {
                const items = await this.getItems(chapter.id!, section.id!);
                for (const item of items) {
                    callback(item, chapter.id!, section.id!);
                }
            }
        }
    }

    public async getItemRewardStatsByType(): Promise<Record<JourneyItemType, JourneyItemRewardStats>> {
        const statsMap = Object.fromEntries(
            JOURNEY_ITEM_TYPES.map((type) => [type, createEmptyRewardStats(type)]),
        ) as Record<JourneyItemType, JourneyItemRewardStats>;

        await this.forEachTemplateItem((item) => {
            const stats = statsMap[item.type];
            if (stats.count === 0) {
                stats.minBrainPoints = item.brainPoints;
                stats.maxBrainPoints = item.brainPoints;
                stats.minEnergyCost = item.energyCost;
                stats.maxEnergyCost = item.energyCost;
            } else {
                stats.minBrainPoints = Math.min(stats.minBrainPoints, item.brainPoints);
                stats.maxBrainPoints = Math.max(stats.maxBrainPoints, item.brainPoints);
                stats.minEnergyCost = Math.min(stats.minEnergyCost, item.energyCost);
                stats.maxEnergyCost = Math.max(stats.maxEnergyCost, item.energyCost);
            }
            stats.count += 1;
        });

        return statsMap;
    }

    public async bulkUpdateItemRewardsByType(
        type: JourneyItemType,
        brainPoints: number,
        energyCost: number,
    ): Promise<{ updatedCount: number }> {
        if (!Number.isFinite(brainPoints) || !Number.isInteger(brainPoints) || brainPoints < 0) {
            throw new Error('Brain points must be a non-negative integer.');
        }
        if (!Number.isFinite(energyCost) || !Number.isInteger(energyCost) || energyCost < 0) {
            throw new Error('Energy cost must be a non-negative integer.');
        }

        let batch = writeBatch(db);
        let operationCount = 0;
        let updatedCount = 0;

        const commitBatchIfNeeded = async () => {
            if (operationCount >= MAX_BATCH_SIZE) {
                await batch.commit();
                batch = writeBatch(db);
                operationCount = 0;
            }
        };

        const chapters = await this.getChapters();
        for (const chapter of chapters) {
            const sections = await this.getSections(chapter.id!);
            for (const section of sections) {
                const items = await this.getItems(chapter.id!, section.id!);
                for (const item of items) {
                    if (item.type !== type) continue;
                    if (item.brainPoints === brainPoints && item.energyCost === energyCost) continue;

                    const itemRef = doc(db, COLLECTION_NAME, chapter.id!, 'sections', section.id!, 'items', item.id!);
                    batch.update(itemRef, { brainPoints, energyCost, updatedAt: serverTimestamp() });
                    operationCount += 1;
                    updatedCount += 1;
                    await commitBatchIfNeeded();
                }
            }
        }

        if (operationCount > 0) {
            await batch.commit();
        }

        return { updatedCount };
    }

    public async getAllSectionOptions(): Promise<JourneySectionOption[]> {
        const chapters = await this.getChapters();
        const perChapter = await Promise.all(
            chapters.map(async (chapter) => {
                const sections = await this.getSections(chapter.id!);
                return sections.map((section) => ({
                    chapterId: chapter.id!,
                    chapterNumber: chapter.number,
                    chapterTitle: chapter.title,
                    sectionId: section.id!,
                    sectionHeader: section.header,
                    sectionOrder: section.order,
                }));
            }),
        );
        return perChapter.flat();
    }

    public async moveItem(
        sourceChapterId: string,
        sourceSectionId: string,
        targetChapterId: string,
        targetSectionId: string,
        item: JourneyItemTemplateFirestore,
    ): Promise<void> {
        // Determine the next order value in the target section
        const targetItems = await this.getItems(targetChapterId, targetSectionId);
        const maxOrder = targetItems.length > 0 ? Math.max(...targetItems.map((i) => i.order)) : -1;

        // Write the item into the target section preserving its original document id
        const { id, ...dataWithoutId } = item;
        const targetRef = doc(db, COLLECTION_NAME, targetChapterId, 'sections', targetSectionId, 'items', id!);
        await setDoc(targetRef, { ...dataWithoutId, order: maxOrder + 1, updatedAt: serverTimestamp() });

        // Remove from the source section
        await this.deleteItem(sourceChapterId, sourceSectionId, id!);

        // Re-number the remaining source items to close the gap
        const sourceItems = await this.getItems(sourceChapterId, sourceSectionId);
        sourceItems.sort((a, b) => a.order - b.order);
        for (let index = 0; index < sourceItems.length; index++) {
            const sourceItem = sourceItems[index];
            if (sourceItem.order !== index) {
                await this.updateItem(sourceChapterId, sourceSectionId, { ...sourceItem, order: index });
            }
        }
    }

    public async deleteSection(chapterId: string, sectionId: string): Promise<void> {
        const MAX_BATCH_SIZE = 500;
        let batch = writeBatch(db);
        let operationCount = 0;

        const commitBatchIfNeeded = async () => {
            if (operationCount >= MAX_BATCH_SIZE) {
                await batch.commit();
                batch = writeBatch(db);
                operationCount = 0;
            }
        };

        const itemsSnapshot = await getDocs(
            collection(db, COLLECTION_NAME, chapterId, 'sections', sectionId, 'items'),
        );
        for (const itemDoc of itemsSnapshot.docs) {
            batch.delete(itemDoc.ref);
            operationCount++;
            await commitBatchIfNeeded();
        }

        batch.delete(doc(db, COLLECTION_NAME, chapterId, 'sections', sectionId));
        operationCount++;

        if (operationCount > 0) {
            await batch.commit();
        }
    }

    public async deleteChapter(chapterId: string): Promise<void> {
        const MAX_BATCH_SIZE = 500;
        let batch = writeBatch(db);
        let operationCount = 0;

        const commitBatchIfNeeded = async () => {
            if (operationCount >= MAX_BATCH_SIZE) {
                await batch.commit();
                batch = writeBatch(db);
                operationCount = 0;
            }
        };

        const sectionsSnapshot = await getDocs(
            collection(db, COLLECTION_NAME, chapterId, 'sections'),
        );

        for (const sectionDoc of sectionsSnapshot.docs) {
            const itemsSnapshot = await getDocs(
                collection(db, COLLECTION_NAME, chapterId, 'sections', sectionDoc.id, 'items'),
            );

            for (const itemDoc of itemsSnapshot.docs) {
                batch.delete(itemDoc.ref);
                operationCount++;
                await commitBatchIfNeeded();
            }

            batch.delete(sectionDoc.ref);
            operationCount++;
            await commitBatchIfNeeded();
        }

        batch.delete(doc(db, COLLECTION_NAME, chapterId));
        operationCount++;

        if (operationCount > 0) {
            await batch.commit();
        }
    }

    public async resetUserJourney(userId: string): Promise<void> {
        const chaptersSnapshot = await getDocs(
            collection(db, 'user_info', userId, 'journey_chapters'),
        );

        const MAX_BATCH_SIZE = 500;
        let batch = writeBatch(db);
        let operationCount = 0;

        const commitBatchIfNeeded = async () => {
            if (operationCount >= MAX_BATCH_SIZE) {
                await batch.commit();
                batch = writeBatch(db);
                operationCount = 0;
            }
        };

        for (const chapterDoc of chaptersSnapshot.docs) {
            const sectionsSnapshot = await getDocs(
                collection(db, 'user_info', userId, 'journey_chapters', chapterDoc.id, 'sections'),
            );

            for (const sectionDoc of sectionsSnapshot.docs) {
                const itemsSnapshot = await getDocs(
                    collection(db, 'user_info', userId, 'journey_chapters', chapterDoc.id, 'sections', sectionDoc.id, 'items'),
                );

                for (const itemDoc of itemsSnapshot.docs) {
                    batch.delete(itemDoc.ref);
                    operationCount++;
                    await commitBatchIfNeeded();
                }

                batch.delete(sectionDoc.ref);
                operationCount++;
                await commitBatchIfNeeded();
            }

            batch.delete(chapterDoc.ref);
            operationCount++;
            await commitBatchIfNeeded();
        }

        if (operationCount > 0) {
            await batch.commit();
        }

        const userRef = doc(db, 'user_info', userId);
        await updateDoc(userRef, { journeyState: deleteField() });
    }

    public async compileUserChapter(chapterNumber: number): Promise<void> {
        await compileUserChapterCall({ chapterNumber });
    }

    public async getUserJourneyState(userId: string): Promise<JourneyState | null> {
        const userRef = doc(db, 'user_info', userId);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) return null;
        const data = userDoc.data();
        return (data?.journeyState as JourneyState) ?? null;
    }

    public async jumpToJourneyPosition(
        userId: string,
        chapterTemplates: JourneyChapterTemplateFirestore[],
        targetChapterTemplateId: string,
        targetSectionTemplateId: string,
        targetItemTemplateId: string,
    ): Promise<void> {
        const targetChapter = chapterTemplates.find((c) => c.id === targetChapterTemplateId);
        if (!targetChapter) throw new Error('Target chapter not found in templates');

        const targetSectionTemplates = await this.getSections(targetChapterTemplateId);
        const targetSection = targetSectionTemplates.find((s) => s.id === targetSectionTemplateId);
        if (!targetSection) throw new Error('Target section not found in templates');

        const targetItemTemplates = await this.getItems(targetChapterTemplateId, targetSectionTemplateId);
        const targetItem = targetItemTemplates.find((i) => i.id === targetItemTemplateId);
        if (!targetItem) throw new Error('Target item not found in templates');

        for (let chapterNum = 1; chapterNum <= targetChapter.number; chapterNum++) {
            await this.compileUserChapter(chapterNum);
        }

        const userChaptersSnapshot = await getDocs(
            collection(db, 'user_info', userId, 'journey_chapters'),
        );

        const MAX_BATCH_SIZE = 500;
        let batch = writeBatch(db);
        let operationCount = 0;

        const commitBatchIfNeeded = async () => {
            if (operationCount >= MAX_BATCH_SIZE) {
                await batch.commit();
                batch = writeBatch(db);
                operationCount = 0;
            }
        };

        for (const chapterDoc of userChaptersSnapshot.docs) {
            const chapterNumber = chapterDoc.data().number as number;

            let chapterStatus: JourneyChapterStatus;
            if (chapterNumber < targetChapter.number) {
                chapterStatus = JourneyChapterStatus.COMPLETED;
            } else if (chapterNumber === targetChapter.number) {
                chapterStatus = JourneyChapterStatus.IN_PROGRESS;
            } else {
                chapterStatus = JourneyChapterStatus.LOCKED;
            }

            batch.update(chapterDoc.ref, { status: chapterStatus, updatedAt: serverTimestamp() });
            operationCount++;
            await commitBatchIfNeeded();

            const sectionsSnapshot = await getDocs(
                collection(db, 'user_info', userId, 'journey_chapters', chapterDoc.id, 'sections'),
            );

            for (const sectionDoc of sectionsSnapshot.docs) {
                const sectionOrder = sectionDoc.data().order as number;

                let sectionStatus: JourneySectionStatus;
                if (chapterStatus === JourneyChapterStatus.COMPLETED) {
                    sectionStatus = JourneySectionStatus.CLAIMED;
                } else if (chapterStatus === JourneyChapterStatus.LOCKED) {
                    sectionStatus = JourneySectionStatus.LOCKED;
                } else if (sectionOrder < targetSection.order) {
                    sectionStatus = JourneySectionStatus.CLAIMED;
                } else {
                    sectionStatus = JourneySectionStatus.LOCKED;
                }

                batch.update(sectionDoc.ref, { status: sectionStatus, updatedAt: serverTimestamp() });
                operationCount++;
                await commitBatchIfNeeded();

                const itemsSnapshot = await getDocs(
                    collection(
                        db, 'user_info', userId, 'journey_chapters',
                        chapterDoc.id, 'sections', sectionDoc.id, 'items',
                    ),
                );

                for (const itemDoc of itemsSnapshot.docs) {
                    const itemOrder = itemDoc.data().order as number;

                    let itemStatus: JourneyItemStatus;
                    if (chapterStatus === JourneyChapterStatus.COMPLETED) {
                        itemStatus = JourneyItemStatus.CLAIMED;
                    } else if (chapterStatus === JourneyChapterStatus.LOCKED) {
                        itemStatus = JourneyItemStatus.LOCKED;
                    } else if (sectionOrder < targetSection.order) {
                        itemStatus = JourneyItemStatus.CLAIMED;
                    } else if (sectionOrder > targetSection.order) {
                        itemStatus = JourneyItemStatus.LOCKED;
                    } else if (itemOrder < targetItem.order) {
                        itemStatus = JourneyItemStatus.CLAIMED;
                    } else if (itemOrder === targetItem.order) {
                        itemStatus = JourneyItemStatus.AVAILABLE;
                    } else {
                        itemStatus = JourneyItemStatus.LOCKED;
                    }

                    batch.update(itemDoc.ref, { status: itemStatus, updatedAt: serverTimestamp() });
                    operationCount++;
                    await commitBatchIfNeeded();
                }
            }
        }

        if (operationCount > 0) {
            await batch.commit();
        }

        const userRef = doc(db, 'user_info', userId);
        await updateDoc(userRef, {
            'journeyState.currentChapterNumber': targetChapter.number,
            'journeyState.updatedAt': serverTimestamp(),
        });
    }

    public async getUserJourneyTree(userId: string): Promise<UserJourneyTree> {
        const chaptersSnapshot = await getDocs(
            query(
                collection(db, 'user_info', userId, 'journey_chapters'),
                orderBy('number', 'asc'),
            ),
        );

        const chapters = await Promise.all(
            chaptersSnapshot.docs.map(async (chapterDoc) => {
                const sectionsSnapshot = await getDocs(
                    query(
                        collection(db, 'user_info', userId, 'journey_chapters', chapterDoc.id, 'sections'),
                        orderBy('order', 'asc'),
                    ),
                );

                const sections = await Promise.all(
                    sectionsSnapshot.docs.map(async (sectionDoc) => {
                        const itemsSnapshot = await getDocs(
                            query(
                                collection(
                                    db, 'user_info', userId, 'journey_chapters',
                                    chapterDoc.id, 'sections', sectionDoc.id, 'items',
                                ),
                                orderBy('order', 'asc'),
                            ),
                        );

                        const items = itemsSnapshot.docs.map((itemDoc) => ({
                            ...itemDoc.data(),
                            id: itemDoc.id,
                        })) as JourneyItemProgressFirestore[];

                        return {
                            ...sectionDoc.data(),
                            id: sectionDoc.id,
                            items,
                        } as JourneySectionProgressFirestore;
                    }),
                );

                return {
                    ...chapterDoc.data(),
                    id: chapterDoc.id,
                    sections,
                } as JourneyChapterProgressFirestore;
            }),
        );

        return chapters;
    }

    public async getJourneyLeaderboard(
        pageSize: number = 20,
        lastDoc?: QueryDocumentSnapshot<DocumentData>,
    ): Promise<{ entries: JourneyLeaderboardEntry[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
        const usersRef = collection(db, 'user_info');
        const constraints = [
            orderBy('journeyState.dayNumber', 'desc'),
            ...(lastDoc ? [startAfter(lastDoc)] : []),
            limit(pageSize),
        ];
        const snapshot = await getDocs(query(usersRef, ...constraints));

        const entries: JourneyLeaderboardEntry[] = [];
        let lastVisible: QueryDocumentSnapshot<DocumentData> | null = null;
        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const state = data?.journeyState;
            if (!state) return;
            entries.push({
                uid: docSnap.id,
                email: data.email ?? null,
                displayName: data.displayName ?? null,
                currentChapterNumber: state.currentChapterNumber ?? 0,
                dayNumber: state.dayNumber ?? 0,
                usableBrainPoints: state.usableBrainPoints ?? 0,
                energy: state.energy ?? 0,
                updatedAt: state.updatedAt instanceof Timestamp ? state.updatedAt.toDate() : null,
            });
            lastVisible = docSnap;
        });
        return { entries, lastDoc: entries.length < pageSize ? null : lastVisible };
    }

    public async updateUserJourneyState(userId: string, patch: Partial<JourneyState>): Promise<void> {
        const userRef = doc(db, 'user_info', userId);
        const updates: Record<string, unknown> = { 'journeyState.updatedAt': serverTimestamp() };
        for (const [key, value] of Object.entries(patch)) {
            updates[`journeyState.${key}`] = value;
        }
        await updateDoc(userRef, updates);
    }

    public async updateUserItemProgress(
        userId: string,
        chapterId: string,
        sectionId: string,
        itemId: string,
        patch: { status?: JourneyItemStatus; progress?: number; maxProgress?: number },
    ): Promise<void> {
        const itemRef = doc(
            db, 'user_info', userId, 'journey_chapters',
            chapterId, 'sections', sectionId, 'items', itemId,
        );
        await updateDoc(itemRef, { ...patch, updatedAt: serverTimestamp() });
    }

    /**
     * Fully resets a chat quest for a user: server progress back to square one, and the
     * quest transcript deleted from the server chat history. The `chatResetAt` marker is
     * still set: older app versions keep the transcript on the phone and use it to throw
     * that copy away, and new versions use it to skip a copy imported from local storage.
     */
    public async resetUserChatItem(
        userId: string,
        chapterId: string,
        sectionId: string,
        itemId: string,
        options: { clearOcdProfile: boolean },
    ): Promise<void> {
        const itemRef = doc(
            db, 'user_info', userId, 'journey_chapters',
            chapterId, 'sections', sectionId, 'items', itemId,
        );
        const itemSnap = await getDoc(itemRef);
        if (!itemSnap.exists()) throw new Error('Journey item not found for this user');

        const item = itemSnap.data() as JourneyItemProgressFirestore;
        if (item.type !== 'chat') throw new Error(`Only chat items can be reset (item is "${item.type}")`);

        const fieldKeys = Object.keys(item.fieldsToCollect ?? {});
        const clearedFields = Object.fromEntries(fieldKeys.map((key) => [key, false]));

        await updateDoc(itemRef, {
            status: JourneyItemStatus.AVAILABLE,
            progress: 0,
            fieldsToCollect: clearedFields,
            chatResetAt: Date.now(),
            updatedAt: serverTimestamp(),
        });

        // Same ID as the app's quest chats (useQuestChatsStore). Deleting a missing document is not an error.
        await deleteDoc(doc(db, 'user_chat_history', userId, 'quest_chats', `chat_quest_${itemId}`));

        if (!options.clearOcdProfile || fieldKeys.length === 0) return;

        // Only `inferred` is cleared — that's the half the chat writes (see
        // finalizeJourneyChat's backfill); anything the user typed in themselves stays.
        const batch = writeBatch(db);
        for (const key of fieldKeys) {
            const profileRef = doc(db, 'user_info', userId, 'ocd_profile', key);
            batch.set(profileRef, { inferred: [], updatedAt: serverTimestamp() }, { merge: true });
        }
        await batch.commit();
    }

    public async updateUserSectionStatus(
        userId: string,
        chapterId: string,
        sectionId: string,
        status: JourneySectionStatus,
    ): Promise<void> {
        const sectionRef = doc(db, 'user_info', userId, 'journey_chapters', chapterId, 'sections', sectionId);
        await updateDoc(sectionRef, { status, updatedAt: serverTimestamp() });
    }

    public async updateUserChapterStatus(
        userId: string,
        chapterId: string,
        status: JourneyChapterStatus,
    ): Promise<void> {
        const chapterRef = doc(db, 'user_info', userId, 'journey_chapters', chapterId);
        await updateDoc(chapterRef, { status, updatedAt: serverTimestamp() });
    }
}

export const journeyService = JourneyService.getInstance();
